import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { LeadsService } from '../../services/leads.service';
import { AsyncPipe } from '@angular/common';
import { takeUntil, take, of, switchMap, tap } from 'rxjs';
import { MenuComponentBase } from '../../../../shared/base-classes/menu-component-base';
import { Company } from '../../../../shared/interfaces/company';
import { AssignedLeadRowComponent } from '../assigned-lead-row/assigned-lead-row.component';
import { PopupComponent } from '../../../../shared/components/popup/popup.component';
import { UnassignLeadPopupComponent } from '../../../../shared/components/unassign-lead-popup/unassign-lead-popup.component';
import { FormsModule } from '@angular/forms';
import { ArcgisWebmapComponent } from '../../../../shared/components/arcgis-webmap/arcgis-webmap.component';
import { InquiryFormLinksComponent } from '../inquiry-form-links/inquiry-form-links.component';
import { PaginatedResponse } from '../../../../shared/interfaces/pagination';
import { PaginationComponent } from '../../../../shared/components/pagination/pagination.component';
import { SvgIconComponent } from '../../../../shared/components/svg-icon/svg-icon.component';
import { RecordDetailsComponent } from '../../../../shared/components/record-details/record-details.component';
import { PageSizeComponent } from '../../../../shared/components/page-size/page-size.component';
import { AuthService } from '../../../auth/services/auth.service';
import { FilterComponent } from '../../../../shared/components/filter/filter.component';
import { LeadStatusValues } from '../../models/lead-status';
import { SearchInputComponent } from '../../../../shared/components/search-input/search-input.component';
import { AuditLogService } from '../../../../shared/services/audit-log/audit-log.service';

@Component({
  selector: 'app-assigned-leads',
  standalone: true,
  imports: [
    AsyncPipe,
    FormsModule,
    AssignedLeadRowComponent,
    PopupComponent,
    ArcgisWebmapComponent,
    InquiryFormLinksComponent,
    PaginationComponent,
    SvgIconComponent,
    RecordDetailsComponent,
    PageSizeComponent,
    FilterComponent,
    SearchInputComponent,
    UnassignLeadPopupComponent,
  ],
  templateUrl: './assigned-leads.component.html',
  styleUrl: './assigned-leads.component.scss',
})
export class AssignedLeadsComponent
  extends MenuComponentBase
  implements OnInit
{
  authService = inject(AuthService);
  leadsService = inject(LeadsService);
  auditLogService = inject(AuditLogService);
  currentPage = 1;
  pageSize = 50;
  totalLeads = 0;
  selectedLeads: Company[] = [];
  showLeadDetails = false;
  selectedLeadForDetailView!: Company;
  showConfirmPopup = false;
  isUnassigning = false;
  popupLead!: Company;
  allChecked = false;
  showWebMap = false;
  showInquiryPopup = false;
  loggedInUser!: string | undefined;
  isMapReady = false;
  pendingMapPoints: Company[] | null = null;
  searchQuery = '';
  leadQuotas = 0;
  filterBy = '';
  filterByOptions = [
    {
      option: 'All',
      value: '',
    },
    {
      option: 'Not set',
      value: LeadStatusValues['not_defined'],
    },
    {
      option: 'Under review',
      value: LeadStatusValues['under_review'],
    },
    {
      option: 'Further information required',
      value: LeadStatusValues['further_information_required'],
    },
    {
      option: 'Prospective client',
      value: LeadStatusValues['prospective_client'],
    },
    {
      option: 'On hold',
      value: LeadStatusValues['on_hold'],
    },
    {
      option: 'Declined',
      value: LeadStatusValues['declined'],
    },
    {
      option: 'Not currently pursuing',
      value: LeadStatusValues['not_currently_pursuing'],
    },
    {
      option: 'Active client',
      value: LeadStatusValues['active_client'],
    },
    {
      option: 'Request lead contact',
      value: LeadStatusValues['request_lead_contact'],
    },
    {
      option: 'Active Funded',
      value: LeadStatusValues['active_funded'],
    },
    {
      option: 'Active Funded Grant',
      value: LeadStatusValues['active_funded_grant'],
    },
    {
      option: 'Active Technical Assistance',
      value: LeadStatusValues['active_technical_assistance'],
    },
  ];

  @ViewChild(ArcgisWebmapComponent)
  arcgisWebmapComponent!: ArcgisWebmapComponent;

  myLeads$ = this.leadsService.leads$;

  override ngOnInit(): void {
    super.ngOnInit();

    // Check for lead_status query parameter
    const leadStatus = this.route.snapshot.queryParamMap.get('lead_status');
    if (leadStatus) {
      this.filterBy = leadStatus;
    }

    this.loadLeads();
    this.loggedInUser = this.authService.getLoggedInUserInfo()?.username;
  }

  loadLeads(): void {
    this.allChecked = false;
    this.selectedLeads = [];
    this.leadsService
      .getLeads(
        this.currentPage,
        this.pageSize,
        false,
        this.searchQuery,
        this.filterBy,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe((response: PaginatedResponse<Company>) => {
        this.totalLeads = response.count;
      });
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadLeads();
  }

  onPageSizeChange(newPageSize: number): void {
    if (newPageSize !== this.pageSize) {
      this.pageSize = newPageSize;
      this.currentPage = 1;
      this.loadLeads();
    }
  }

  onLeadChecked({ lead, checked }: { lead: Company; checked: boolean }) {
    if (checked) {
      this.selectedLeads.push(lead);
    } else {
      this.selectedLeads = this.selectedLeads.filter((item) => item !== lead);
    }
  }

  onAllCheckBoxChange(leads: Company[]): void {
    if (this.allChecked) {
      this.selectedLeads = leads;
    } else {
      this.selectedLeads = [];
    }
  }

  onUnassignLead(lead: Company): void {
    this.popupLead = lead;
    this.showConfirmPopup = true;
  }

  getLeadCurrentStatus(lead?: Company): string | number | null {
    if (!lead) return null;

    const isRealStatus = (val: unknown): boolean => {
      if (val === null || val === undefined || val === '') return false;
      const str = String(val).trim().toLowerCase();
      return ![
        '0',
        'null',
        'undefined',
        'not defined',
        'not_defined',
        'not set',
        'not_set',
        'select lead status',
      ].includes(str);
    };

    const userConnection = this.authService
      .getLoggedInUserInfo()
      ?.connection?.toLowerCase();
    const userUsername = this.loggedInUser?.toLowerCase();
    const currentUserAssignee = lead.assignees?.find(
      (a) =>
        (userConnection &&
          a.connection_name?.toLowerCase() === userConnection) ||
        (userUsername && a.connection_name?.toLowerCase() === userUsername) ||
        (userUsername &&
          (a as { username?: string }).username?.toLowerCase() ===
            userUsername),
    );

    const candidates = [
      lead.lead_status,
      (lead as unknown as { leadStatus?: string | number }).leadStatus,
      lead.lead_attributes?.['lead_status'],
      currentUserAssignee?.lead_status,
      lead.assignees?.[0]?.lead_status,
    ];

    for (const val of candidates) {
      if (isRealStatus(val)) {
        return val as string | number;
      }
    }

    for (const val of candidates) {
      if (val !== undefined && val !== null && val !== '') {
        return val as string | number;
      }
    }

    return null;
  }

  OnConfirmUnassign(event?: {
    selectedStatus?: string;
    statusNotes?: string;
  }): void {
    const newLeadStatus = event?.selectedStatus;
    const newStatusNotes = event?.statusNotes;
    this.isUnassigning = true;

    this.auditLogService.logFeature({
      featureName: 'unassigned_leads',
      action: 'unassign',
      meta: {
        leadId: String(this.popupLead.leadId || ''),
        leadName: this.popupLead.company_name,
      },
      description: 'User unassigns a lead from themselves',
    });

    if (newStatusNotes !== undefined && this.popupLead?.lead_attributes) {
      this.popupLead.lead_attributes['status_notes'] = newStatusNotes;
    }

    const statusUpdate$ = newLeadStatus
      ? this.leadsService
          .updateLeadStatus([
            {
              unique_id: this.popupLead.unique_id,
              lead_status: newLeadStatus,
            },
          ])
          .pipe(
            tap(() => {
              this.leadsService.triggerStatusCountRefresh();
              if (this.popupLead?.lead_attributes) {
                this.popupLead.lead_attributes['lead_status'] =
                  Number(newLeadStatus) || newLeadStatus;
              }
            }),
          )
      : of(null);

    statusUpdate$
      .pipe(
        switchMap(() =>
          this.leadsService.unassignLead(this.popupLead.unique_id),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: () => {
          this.isUnassigning = false;
          this.showConfirmPopup = false;
          this.currentPage =
            this.totalLeads == 1 ? this.currentPage - 1 : this.currentPage;
          if (this.currentPage == 0) this.currentPage = 1;
          this.loadLeads();
        },
        error: (err) => {
          this.isUnassigning = false;
          console.error(err);
        },
      });
  }

  showDetailView(lead: Company): void {
    this.selectedLeadForDetailView = lead;
    this.showLeadDetails = true;
  }

  hideDetailView(): void {
    this.showLeadDetails = false;
  }

  onShowInMap(): void {
    if (this.selectedLeads.length > 0) {
      this.showWebMap = true;
      this.pendingMapPoints = this.selectedLeads;

      // If map is already ready, show points immediately
      if (this.isMapReady && this.arcgisWebmapComponent) {
        this.arcgisWebmapComponent.showMapPoints(this.pendingMapPoints);
        this.pendingMapPoints = null;
      }
    }
  }

  onMapReady(): void {
    this.isMapReady = true;

    // If there are pending points to show, show them now
    if (this.pendingMapPoints && this.arcgisWebmapComponent) {
      this.arcgisWebmapComponent.showMapPoints(this.pendingMapPoints);
      this.pendingMapPoints = null;
    }
  }

  onGenerateInquiryForm(): void {
    this.showInquiryPopup = true;
  }

  hideInquiryPopup(): void {
    this.showInquiryPopup = false;
  }

  onCompanyDetailsUpdated(updatedCompany: Company): void {
    // Update in the list
    this.myLeads$.pipe(take(1)).subscribe((leads) => {
      if (leads) {
        const index = leads.findIndex(
          (l) => l.unique_id === updatedCompany.unique_id,
        );
        if (index !== -1) {
          leads[index] = updatedCompany;
          this.leadsService.updateLeads(leads);
        }
      }
    });
  }

  onCompanyDeleted(uniqueId: string): void {
    this.myLeads$.pipe(take(1)).subscribe((leads) => {
      if (leads) {
        const filteredLeads = leads.filter(
          (lead) => lead.unique_id !== uniqueId,
        );
        this.leadsService.updateLeads(filteredLeads);

        // Update total counts
        this.totalLeads = Math.max(0, this.totalLeads - 1);

        // If current page is empty and not the first page, go to previous page
        if (filteredLeads.length === 0 && this.currentPage > 1) {
          this.onPageChange(this.currentPage - 1);
        }
      }
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onLeadStatusUpdated(event: { uniqueId: string; newStatus: string }): void {
    // Reload leads as the status change may affect whether it matches current filter
    this.loadLeads();
  }

  onSearchQueryChange(query: string) {
    this.searchQuery = query;
    this.currentPage = 1;
    this.loadLeads();
  }

  onFilterByChange(value: string): void {
    this.filterBy = value;
    this.currentPage = 1;
    this.loadLeads();
  }
}
